import { isTeamCoOwnerActor, isTeamOwnerActor } from './teamConfig';
import {
  CCT_APPROVED_SERVICES_CONFIRMED,
  COMPARISON_DATASET_OPTIONS,
  CONDITION_FIELD_OPTIONS,
  CONTENT_LANGUAGE_OPTIONS,
  DETECTION_SOURCE_OPTIONS,
  FILE_TYPE_OPTIONS,
  LOOKBACK_UNIT_OPTIONS,
  fileTypeApplies,
  getApprovedPatternsForIssue,
  getAvailableDetectionMethods,
  getCommonFileTypes,
  getCommonLanguages,
  getContentAreasForModule,
  getContentContextsForArea,
  getContentIssuesForArea,
  getDetectionServiceOptions,
  getFileTypesForContentArea,
  getMethodSource,
  getSpamContextsForIssue,
  getTriggersForScope,
  isPrimaryMethod,
  isScopeCombinationValid,
  isSupportingMethod,
  languageApplies,
  nextContentControlRuleId,
  NORMALISATION_METHODS,
  normalizeContentControlForm,
  operatorIsMulti,
  requiresImageContactSupportingChecks,
  TEXT_BASED_METHODS,
} from './contentControlConfig';
import { emitRuleSaveHistory } from './changeHistoryEmitters';

const ACTOR = {
  name: 'Olivia Brown',
  role: 'Configuration Admin',
  team: 'Platform Operations',
  accountId: 'ADM-0002',
};

export function contentControlActor() {
  return { ...ACTOR };
}

export function canAccessContentControl(auth) {
  return isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth);
}

export function canViewContentControl(auth) {
  return canAccessContentControl(auth);
}

export function canAddContentControlRule(auth) {
  return canAccessContentControl(auth);
}

export function canEditContentControlRule(auth) {
  return canAccessContentControl(auth);
}

export function canSaveContentControlRule(auth) {
  return canAccessContentControl(auth);
}

function requiredMessage(field) {
  return `${field} is required.`;
}

function uniqueSorted(values = []) {
  return [...new Set((values || []).filter((value) => value != null && value !== ''))].sort();
}

function sameSet(a = [], b = []) {
  const left = uniqueSorted(a);
  const right = uniqueSorted(b);
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

export function applyNormalisation(value, controls = []) {
  let next = String(value || '').trim();
  if (controls.includes('ignore_extra_spaces')) next = next.replace(/\s+/g, ' ');
  if (controls.includes('ignore_letter_case')) next = next.toLowerCase();
  if (controls.includes('ignore_punctuation')) next = next.replace(/[^\p{L}\p{N}\s]/gu, '');
  return next;
}

function listHasNormalisedDuplicates(values, controls) {
  const seen = new Set();
  return (values || []).some((value) => {
    const key = applyNormalisation(value, controls);
    if (!key) return false;
    if (seen.has(key)) return true;
    seen.add(key);
    return false;
  });
}

function conditionValues(condition) {
  return uniqueSorted(condition?.values || []);
}

function conditionKey(condition) {
  return [condition.field || '', condition.operator || '', conditionValues(condition).join(',')].join('|');
}

function sortedConditionKeys(conditions = []) {
  return [...(conditions || []).map(conditionKey)].sort();
}

export function getFieldUniverse(rule, field) {
  if (field === 'file_type') return getFileTypesForContentArea(rule.contentArea);
  if (field === 'content_language') {
    if (!languageApplies(rule.contentArea, rule.detectionMethods)) return [];
    const common = getCommonLanguages(rule.detectionMethods);
    return common.length ? common : CONTENT_LANGUAGE_OPTIONS.map((o) => o.value);
  }
  return [];
}

export function resolveFieldAllowedValues(conditions = [], field, universe = []) {
  let allowed = new Set(universe);
  const fieldConditions = (conditions || []).filter((condition) => condition.field === field);
  fieldConditions.forEach((condition) => {
    const values = conditionValues(condition);
    if (!condition.operator || !values.length) return;
    if (condition.operator === 'equals') {
      allowed = new Set([...allowed].filter((value) => value === values[0]));
    } else if (condition.operator === 'does_not_equal') {
      allowed = new Set([...allowed].filter((value) => value !== values[0]));
    } else if (condition.operator === 'is_any_of') {
      allowed = new Set([...allowed].filter((value) => values.includes(value)));
    } else if (condition.operator === 'is_not_any_of') {
      allowed = new Set([...allowed].filter((value) => !values.includes(value)));
    }
  });
  return [...allowed];
}

export function conditionsAreImpossible(conditions = [], rule) {
  const fields = [...new Set((conditions || []).map((condition) => condition.field).filter(Boolean))];
  return fields.some((field) => {
    const universe = getFieldUniverse(rule, field);
    if (!universe.length) return false;
    return resolveFieldAllowedValues(conditions, field, universe).length === 0;
  });
}

export function getImplicitAllowedValues(rule, field) {
  const universe = getFieldUniverse(rule, field);
  let allowed = resolveFieldAllowedValues(rule.conditions, field, universe);
  if (field === 'file_type') {
    const common = getCommonFileTypes(rule.contentArea, rule.detectionMethods);
    if (common.length) allowed = allowed.filter((value) => common.includes(value));
  }
  if (field === 'content_language') {
    const common = getCommonLanguages(rule.detectionMethods);
    if (common.length) allowed = allowed.filter((value) => common.includes(value));
  }
  return allowed;
}

/** Negative / unrestricted Conditions also cover future catalogue values outside their exclusions. */
function fieldHasOpenFutureCoverage(rule, field) {
  const fieldConditions = (rule.conditions || []).filter(
    (condition) => condition.field === field && condition.operator && conditionValues(condition).length,
  );
  if (!fieldConditions.length) return true;
  const hasPositive = fieldConditions.some(
    (condition) => condition.operator === 'equals' || condition.operator === 'is_any_of',
  );
  // Positive Equals / Is Any Of closes coverage to listed values; negatives alone remain open.
  return !hasPositive;
}

export function rulesOverlap(a, b) {
  if (a.module !== b.module || a.contentArea !== b.contentArea || a.contentContext !== b.contentContext || a.contentIssue !== b.contentIssue) {
    return false;
  }
  const fields = [];
  if (fileTypeApplies(a.contentArea) && fileTypeApplies(b.contentArea)) fields.push('file_type');
  if (languageApplies(a.contentArea, a.detectionMethods) && languageApplies(b.contentArea, b.detectionMethods)) {
    fields.push('content_language');
  }
  if (!fields.length) return true;
  return fields.every((field) => {
    const aValues = getImplicitAllowedValues(a, field);
    const bValues = getImplicitAllowedValues(b, field);
    if (aValues.some((value) => bValues.includes(value))) return true;
    // Expanding value lists: open negative coverage overlaps on future compatible values.
    return fieldHasOpenFutureCoverage(a, field) && fieldHasOpenFutureCoverage(b, field);
  });
}

function detectionConfigKey(rule) {
  return [
    rule.detectionSource || '',
    rule.detectionService || '',
    uniqueSorted(rule.detectionMethods).join(','),
    uniqueSorted((rule.exactTextValues || []).map((value) => applyNormalisation(value, rule.normalisationControls))).join('||'),
    uniqueSorted((rule.wordOrPhraseValues || []).map((value) => applyNormalisation(value, rule.normalisationControls))).join('||'),
    uniqueSorted(rule.approvedPatterns).join(','),
    uniqueSorted(rule.protectedTerms).join(','),
    uniqueSorted(rule.normalisationControls).join(','),
    rule.comparisonDataset || '',
    String(rule.similarityThreshold || ''),
    String(rule.repetitionCount || ''),
    String(rule.lookbackPeriodValue || ''),
    rule.lookbackPeriodUnit || '',
    rule.spamContext || '',
  ].join('|');
}

function responseKey(rule) {
  return [
    'block_current_action',
    'no_review_item_required',
    'block_current_action',
    'no_review_item_required',
    String(rule.blockMessage || '').trim(),
  ].join('|');
}

function settingsDiffer(a, b) {
  return detectionConfigKey(a) !== detectionConfigKey(b) || responseKey(a) !== responseKey(b) || !sameSet(a.triggers, b.triggers);
}

function sameScope(a, b) {
  return (
    a.module === b.module &&
    a.contentArea === b.contentArea &&
    a.contentContext === b.contentContext &&
    a.contentIssue === b.contentIssue
  );
}

function isExactDuplicate(a, b) {
  return (
    sameScope(a, b) &&
    sameSet(a.triggers, b.triggers) &&
    sortedConditionKeys(a.conditions).join('||') === sortedConditionKeys(b.conditions).join('||') &&
    detectionConfigKey(a) === detectionConfigKey(b) &&
    responseKey(a) === responseKey(b)
  );
}

export function needsBlockMessage() {
  return true;
}

export function actionNeedsMessage(action) {
  return action === 'block_current_action';
}

export function needsWarningFields() {
  return false;
}

export function needsHoldMessage() {
  return false;
}

export function getAvailableConditionFields(form) {
  const fields = [];
  if (languageApplies(form.contentArea, form.detectionMethods)) {
    const languages = getCommonLanguages(form.detectionMethods);
    const restrictionNeeded = languages.length && languages.length < CONTENT_LANGUAGE_OPTIONS.length;
    if (restrictionNeeded || CONTENT_LANGUAGE_OPTIONS.length > 1) fields.push('content_language');
  }
  if (fileTypeApplies(form.contentArea)) {
    const types = getCommonFileTypes(form.contentArea, form.detectionMethods);
    const areaTypes = getFileTypesForContentArea(form.contentArea);
    const restrictionNeeded = types.length && types.length < areaTypes.length;
    if (restrictionNeeded || areaTypes.length > 1) fields.push('file_type');
  }
  return CONDITION_FIELD_OPTIONS.filter((field) => fields.includes(field.value));
}

export function getConditionValueOptions(form, field) {
  if (field === 'file_type') {
    return FILE_TYPE_OPTIONS.filter((option) => getCommonFileTypes(form.contentArea, form.detectionMethods).includes(option.value));
  }
  if (field === 'content_language') {
    return CONTENT_LANGUAGE_OPTIONS.filter((option) => getCommonLanguages(form.detectionMethods).includes(option.value));
  }
  return [];
}

function addError(result, key, message) {
  if (!result.summary.includes(message)) result.summary.push(message);
  if (key && !result.errors[key]) result.errors[key] = message;
}

export function collectContentControlSaveErrors(
  formData,
  allRules,
  excludeId,
  { originalLastUpdated, originalScopeKey, canSave = true, checksAvailable = true } = {},
) {
  const result = { errors: {}, summary: [] };

  if (!checksAvailable) {
    addError(result, 'checksUnavailable', 'The Rule could not be checked. Try again.');
    return result;
  }
  if (!canSave) {
    addError(result, 'permission', 'You do not have permission to save this Rule.');
    return result;
  }

  let form;
  try {
    form = normalizeContentControlForm(formData);
  } catch {
    addError(result, 'checksUnavailable', 'The Rule could not be checked. Try again.');
    return result;
  }

  const inOptions = (options, value) => options.some((option) => option.value === value);

  if (!form.ruleName.trim()) addError(result, 'ruleName', requiredMessage('Rule Name'));
  if (!form.description.trim()) addError(result, 'description', requiredMessage('Description'));
  if (!form.module) addError(result, 'module', requiredMessage('Module'));
  if (!form.contentArea) addError(result, 'contentArea', requiredMessage('Content Area'));
  if (!form.contentContext) addError(result, 'contentContext', requiredMessage('Content Context'));
  if (!form.triggers.length) addError(result, 'triggers', requiredMessage('Triggers'));
  if (!form.contentIssue) addError(result, 'contentIssue', requiredMessage('Content Issue'));
  if (!form.detectionSource || !inOptions(DETECTION_SOURCE_OPTIONS, form.detectionSource)) {
    addError(result, 'detectionSource', requiredMessage('Detection Source'));
  }
  if (!String(form.changeReason || '').trim()) addError(result, 'changeReason', 'Enter a Change Reason.');

  if (excludeId && originalScopeKey) {
    const currentKey = [form.module, form.contentArea, form.contentContext, form.contentIssue].join('|');
    if (currentKey !== originalScopeKey) {
      addError(result, 'fixedScope', 'This value cannot be changed.');
    }
  }

  const areaOptions = getContentAreasForModule(form.module);
  if (form.module && form.contentArea && !areaOptions.some((option) => option.value === form.contentArea)) {
    addError(result, 'contentArea', 'Select a compatible value.');
  }
  const contextOptions = getContentContextsForArea(form.module, form.contentArea);
  if (form.contentArea && form.contentContext && !contextOptions.some((option) => option.value === form.contentContext)) {
    addError(result, 'contentContext', 'Select a compatible value.');
  }
  const triggerOptions = getTriggersForScope(form.module, form.contentArea, form.contentContext);
  if (form.triggers.some((trigger) => !triggerOptions.some((option) => option.value === trigger))) {
    addError(result, 'triggers', 'Select a compatible value.');
  }
  if (form.module && form.contentArea && form.contentContext && form.triggers.length && !isScopeCombinationValid(form.module, form.contentArea, form.contentContext, form.triggers)) {
    addError(result, 'contentContext', 'Select a compatible value.');
  }
  const issueOptions = getContentIssuesForArea(form.contentArea);
  if (form.contentIssue && !issueOptions.some((option) => option.value === form.contentIssue)) {
    addError(result, 'contentIssue', 'Select a compatible value.');
  }

  if (form.detectionSource === 'approved_integration' || form.detectionSource === 'combined') {
    if (!CCT_APPROVED_SERVICES_CONFIRMED) {
      addError(result, 'detectionSource', 'This selection is unavailable. Select a supported value.');
    } else {
      if (!form.detectionService) addError(result, 'detectionService', 'Select a Detection Service compatible with every selected integration Method.');
      const serviceOptions = getDetectionServiceOptions(form);
      if (form.detectionService && !serviceOptions.some((option) => option.value === form.detectionService)) {
        addError(result, 'detectionService', 'Select a Detection Service compatible with every selected integration Method.');
      }
    }
  }

  const availableMethods = getAvailableDetectionMethods(form);
  if (form.detectionMethods.some((method) => !availableMethods.includes(method))) {
    addError(result, 'detectionMethods', 'Select a compatible value.');
  }

  const primaryMethods = form.detectionMethods.filter(isPrimaryMethod);
  const supportingMethods = form.detectionMethods.filter(isSupportingMethod);
  if (!primaryMethods.length) addError(result, 'detectionMethods', 'Select at least one Primary Detection Method.');
  if (supportingMethods.length && !primaryMethods.length) {
    addError(result, 'detectionMethods', 'Select a compatible Primary Method.');
  }

  if (requiresImageContactSupportingChecks(form.contentArea, form.contentIssue)) {
    const hasOcr = form.detectionMethods.includes('ocr_text_detection');
    const hasQr = form.detectionMethods.includes('qr_code_or_barcode_detection');
    const hasContactPrimary = form.detectionMethods.includes('contact_and_link_detection')
      || form.detectionMethods.some((method) => ['exact_text_match', 'word_or_phrase_match', 'pattern_and_context_detection'].includes(method));
    if (!hasOcr || !hasQr || !hasContactPrimary) {
      addError(result, 'detectionMethods', 'Select OCR Text Detection and QR-Code or Barcode Detection.');
    }
  }

  if (form.detectionSource === 'combined' && CCT_APPROVED_SERVICES_CONFIRMED) {
    const hasInternal = primaryMethods.some((method) => ['internal', 'both'].includes(getMethodSource(method, form.detectionSource, form.detectionService)));
    const hasService = primaryMethods.some((method) => ['integration', 'both'].includes(getMethodSource(method, form.detectionSource, form.detectionService)));
    if (!hasInternal || !hasService) {
      addError(result, 'detectionMethods', 'Select an internal Primary Method and a selected-service Primary Method.');
    }
  }

  if (form.detectionMethods.includes('exact_text_match')) {
    const values = (form.exactTextValues || []).map((value) => String(value || '').trim()).filter(Boolean);
    if (!values.length) addError(result, 'exactTextValues', 'Complete the settings for Exact Text Match.');
    else if (listHasNormalisedDuplicates(values, form.normalisationControls)) {
      addError(result, 'exactTextValues', 'Remove the duplicate value from Exact Text Values.');
    } else if ((form.exactTextValues || []).some((value) => !String(applyNormalisation(value, form.normalisationControls) || '').trim())) {
      addError(result, 'exactTextValues', 'Enter a value that remains non-blank after normalisation.');
    }
  }
  if (form.detectionMethods.includes('word_or_phrase_match')) {
    const values = (form.wordOrPhraseValues || []).map((value) => String(value || '').trim()).filter(Boolean);
    if (!values.length) addError(result, 'wordOrPhraseValues', 'Complete the settings for Word or Phrase Match.');
    else if (listHasNormalisedDuplicates(values, form.normalisationControls)) {
      addError(result, 'wordOrPhraseValues', 'Remove the duplicate value from Word or Phrase Values.');
    } else if ((form.wordOrPhraseValues || []).some((value) => !String(applyNormalisation(value, form.normalisationControls) || '').trim())) {
      addError(result, 'wordOrPhraseValues', 'Enter a value that remains non-blank after normalisation.');
    }
  }
  if (form.detectionMethods.includes('pattern_and_context_detection')) {
    const allowed = getApprovedPatternsForIssue(form.contentIssue).map((option) => option.value);
    if (!form.approvedPatterns.length) addError(result, 'approvedPatterns', 'Complete the settings for Pattern and Context Detection.');
    else if (form.approvedPatterns.some((value) => !allowed.includes(value))) {
      addError(result, 'approvedPatterns', 'Select a compatible value.');
    }
  }
  if (form.detectionMethods.includes('protected_identity_detection') && !form.protectedTerms.length) {
    addError(result, 'protectedTerms', 'Complete the settings for Protected Identity Detection.');
  }
  if (form.detectionMethods.includes('similarity_and_spam_repetition_detection')) {
    if (!form.comparisonDataset || !COMPARISON_DATASET_OPTIONS.some((option) => option.value === form.comparisonDataset)) {
      addError(result, 'comparisonDataset', 'Complete the settings for Similarity and Spam-Repetition Detection.');
    }
    const threshold = Number(form.similarityThreshold);
    if (!Number.isInteger(threshold) || threshold < 1 || threshold > 100) {
      addError(result, 'similarityThreshold', 'Similarity Threshold must be a whole number from 1% to 100%.');
    }
    const count = Number(form.repetitionCount);
    if (!Number.isInteger(count) || count < 2) {
      addError(result, 'repetitionCount', 'Repetition Count must be a whole number of 2 or more.');
    }
    const lookback = Number(form.lookbackPeriodValue);
    if (!Number.isInteger(lookback) || lookback <= 0 || !form.lookbackPeriodUnit || !LOOKBACK_UNIT_OPTIONS.some((option) => option.value === form.lookbackPeriodUnit)) {
      addError(result, 'lookbackPeriodValue', 'Enter a positive whole number and time unit.');
    }
    const spamOptions = getSpamContextsForIssue(form.contentIssue);
    if (!form.spamContext) addError(result, 'spamContext', 'Complete the settings for Similarity and Spam-Repetition Detection.');
    else if (!spamOptions.some((option) => option.value === form.spamContext)) addError(result, 'spamContext', 'Select a compatible value.');
  }

  if (fileTypeApplies(form.contentArea)) {
    const commonTypes = getCommonFileTypes(form.contentArea, form.detectionMethods);
    const areaTypes = getFileTypesForContentArea(form.contentArea);
    if (form.detectionMethods.length && !commonTypes.length) {
      addError(result, 'detectionMethods', 'These Methods have no common supported coverage.');
    } else if (commonTypes.length >= 2 && commonTypes.length < areaTypes.length) {
      const hasFileTypeCondition = (form.conditions || []).some((condition) => condition.field === 'file_type' && condition.operator && conditionValues(condition).length);
      if (!hasFileTypeCondition) {
        addError(result, 'conditions', 'Add a supported File Type Condition.');
      }
    }
  }
  if (languageApplies(form.contentArea, form.detectionMethods)) {
    const commonLanguages = getCommonLanguages(form.detectionMethods);
    if (form.detectionMethods.some((method) => TEXT_BASED_METHODS.has(method)) && !commonLanguages.length) {
      addError(result, 'detectionMethods', 'These Methods have no common supported coverage.');
    } else if (commonLanguages.length >= 2 && commonLanguages.length < CONTENT_LANGUAGE_OPTIONS.length) {
      const hasLanguageCondition = (form.conditions || []).some((condition) => condition.field === 'content_language' && condition.operator && conditionValues(condition).length);
      if (!hasLanguageCondition) {
        addError(result, 'conditions', 'Add a supported Content Language Condition.');
      }
    }
  }

  const seenConditionKeys = new Set();
  (form.conditions || []).forEach((condition, index) => {
    const key = `condition_${index}`;
    const incomplete = !condition.field || !condition.operator || !conditionValues(condition).length;
    if (incomplete) {
      addError(result, key, 'Complete or remove this Condition.');
      return;
    }
    const availableFields = getAvailableConditionFields(form).map((option) => option.value);
    if (!availableFields.includes(condition.field)) {
      addError(result, key, 'Select a compatible value.');
    }
    const valueOptions = getConditionValueOptions(form, condition.field).map((option) => option.value);
    if (conditionValues(condition).some((value) => !valueOptions.includes(value))) {
      addError(result, key, 'Select a compatible value.');
    }
    if (!operatorIsMulti(condition.operator) && conditionValues(condition).length !== 1) {
      addError(result, key, 'Complete or remove this Condition.');
    }
    const values = conditionValues(condition);
    if (values.length !== (condition.values || []).filter(Boolean).length) {
      addError(result, key, `Remove the duplicate value from ${condition.field === 'file_type' ? 'File Type' : 'Content Language'}.`);
    }
    const dupKey = conditionKey(condition);
    if (seenConditionKeys.has(dupKey)) addError(result, key, 'This Condition is duplicated.');
    seenConditionKeys.add(dupKey);
  });

  if (conditionsAreImpossible(form.conditions, form)) {
    addError(result, 'conditions', 'These Conditions cannot all match.');
  }

  if (!form.blockMessage.trim()) addError(result, 'blockMessage', 'Enter a Block Message.');

  try {
    const others = (allRules || []).filter((rule) => rule.id !== excludeId).map(normalizeContentControlForm);
    const duplicate = others.find((rule) => isExactDuplicate(rule, form));
    if (duplicate) {
      addError(result, 'duplicate', `This Rule duplicates ${duplicate.id}.`);
    } else {
      const sameScopeRules = others.filter((rule) => sameScope(rule, form));
      const noConditionPeer = sameScopeRules.find((rule) => !(rule.conditions || []).length);
      if (!(form.conditions || []).length && sameScopeRules.length) {
        if (noConditionPeer) addError(result, 'overlap', 'A Rule with No Conditions already covers this scope.');
        else addError(result, 'overlap', 'This Rule cannot use No Conditions because conditional Rules already cover this scope.');
      } else if ((form.conditions || []).length && noConditionPeer) {
        addError(result, 'overlap', 'A Rule with No Conditions already covers this scope.');
      } else {
        const overlapping = sameScopeRules.find((rule) => rulesOverlap(rule, form));
        if (overlapping) {
          const triggerOnly = !sameSet(overlapping.triggers, form.triggers);
          if (triggerOnly && detectionConfigKey(overlapping) === detectionConfigKey(form) && responseKey(overlapping) === responseKey(form)) {
            addError(result, 'overlap', 'Add this Trigger to the existing Rule or use mutually exclusive Conditions.');
          } else if (settingsDiffer(overlapping, form)) {
            addError(result, 'conflict', `This Rule conflicts with ${overlapping.id}.`);
          } else {
            addError(result, 'overlap', `This Rule overlaps ${overlapping.id}. Change its Conditions or edit the existing Rule.`);
          }
        }
      }
    }
  } catch {
    addError(result, 'checksUnavailable', 'The Rule could not be checked. Try again.');
  }

  if (excludeId && originalLastUpdated) {
    const current = (allRules || []).find((rule) => rule.id === excludeId);
    if (current?.lastUpdated && current.lastUpdated !== originalLastUpdated) {
      addError(result, 'concurrent', 'This Rule has changed. Review the latest saved Rule before saving.');
    }
  }

  return result;
}

export function contentControlScopeKey(rule) {
  return [rule?.module || '', rule?.contentArea || '', rule?.contentContext || '', rule?.contentIssue || ''].join('|');
}

export function contentControlFingerprint(rule) {
  const snapshot = ruleSnapshotFromForm(rule);
  return JSON.stringify({
    ruleName: snapshot.ruleName,
    description: snapshot.description,
    module: snapshot.module,
    contentArea: snapshot.contentArea,
    contentContext: snapshot.contentContext,
    contentIssue: snapshot.contentIssue,
    triggers: uniqueSorted(snapshot.triggers),
    detectionSource: snapshot.detectionSource,
    detectionService: snapshot.detectionService,
    detectionMethods: uniqueSorted(snapshot.detectionMethods),
    exactTextValues: snapshot.exactTextValues,
    wordOrPhraseValues: snapshot.wordOrPhraseValues,
    approvedPatterns: uniqueSorted(snapshot.approvedPatterns),
    protectedTerms: uniqueSorted(snapshot.protectedTerms),
    normalisationControls: uniqueSorted(snapshot.normalisationControls),
    comparisonDataset: snapshot.comparisonDataset,
    similarityThreshold: snapshot.similarityThreshold,
    repetitionCount: snapshot.repetitionCount,
    lookbackPeriodValue: snapshot.lookbackPeriodValue,
    lookbackPeriodUnit: snapshot.lookbackPeriodUnit,
    spamContext: snapshot.spamContext,
    conditions: sortedConditionKeys(snapshot.conditions),
    blockMessage: snapshot.blockMessage,
  });
}

export function ruleSnapshotFromForm(formData) {
  const form = normalizeContentControlForm(formData);
  const normalisation = form.detectionMethods.some((method) => NORMALISATION_METHODS.has(method))
    ? uniqueSorted(form.normalisationControls)
    : [];
  return {
    ruleName: form.ruleName.trim(),
    description: form.description.trim(),
    module: form.module,
    contentArea: form.contentArea,
    contentContext: form.contentContext,
    triggers: uniqueSorted(form.triggers),
    contentIssue: form.contentIssue,
    detectionSource: form.detectionSource,
    detectionService: form.detectionSource === 'internal' ? '' : form.detectionService,
    detectionMethods: uniqueSorted(form.detectionMethods),
    exactTextValues: form.detectionMethods.includes('exact_text_match')
      ? (form.exactTextValues || []).map((value) => String(value || '').trim()).filter(Boolean)
      : [],
    wordOrPhraseValues: form.detectionMethods.includes('word_or_phrase_match')
      ? (form.wordOrPhraseValues || []).map((value) => String(value || '').trim()).filter(Boolean)
      : [],
    approvedPatterns: form.detectionMethods.includes('pattern_and_context_detection') ? uniqueSorted(form.approvedPatterns) : [],
    protectedTerms: form.detectionMethods.includes('protected_identity_detection') ? uniqueSorted(form.protectedTerms) : [],
    normalisationControls: normalisation,
    comparisonDataset: form.detectionMethods.includes('similarity_and_spam_repetition_detection') ? form.comparisonDataset : '',
    similarityThreshold: form.detectionMethods.includes('similarity_and_spam_repetition_detection') ? Number(form.similarityThreshold) : '',
    repetitionCount: form.detectionMethods.includes('similarity_and_spam_repetition_detection') ? Number(form.repetitionCount) : '',
    lookbackPeriodValue: form.detectionMethods.includes('similarity_and_spam_repetition_detection') ? Number(form.lookbackPeriodValue) : '',
    lookbackPeriodUnit: form.detectionMethods.includes('similarity_and_spam_repetition_detection') ? form.lookbackPeriodUnit : '',
    spamContext: form.detectionMethods.includes('similarity_and_spam_repetition_detection') ? form.spamContext : '',
    conditions: (form.conditions || [])
      .filter((condition) => condition.field && condition.operator && conditionValues(condition).length)
      .map((condition) => ({
        field: condition.field,
        operator: condition.operator,
        values: conditionValues(condition),
      })),
    confirmedAction: 'block_current_action',
    confirmedReviewRequirement: 'no_review_item_required',
    suspectedAction: 'block_current_action',
    suspectedReviewRequirement: 'no_review_item_required',
    noMatchBehaviour: 'continue_current_action',
    blockMessage: form.blockMessage.trim(),
    holdMessage: '',
    warningTitle: '',
    warningMessage: '',
    warningResponse: '',
  };
}

export function getPublishedRule(rule) {
  if (!rule) return null;
  const { pendingDraft, ...published } = rule;
  return normalizeContentControlForm(published);
}

export function getEditRuleSource(rule) {
  if (!rule) return null;
  return normalizeContentControlForm(rule);
}

export function appendTestHistory(rule, summary, passed) {
  const actor = contentControlActor();
  const base = Array.isArray(rule.testHistory) ? rule.testHistory : [];
  return [
    ...base,
    {
      actor: actor.name,
      role: actor.role,
      accountId: actor.accountId,
      timestamp: new Date().toISOString(),
      result: passed ? 'passed' : 'blocked',
      summary,
    },
  ];
}

export function mergeRulesAfterSave(allRules, payload, publishedBaseline) {
  const snapshot = ruleSnapshotFromForm(payload);
  const isNew = !publishedBaseline?.id;
  const id = payload.id || publishedBaseline?.id || nextContentControlRuleId(allRules);

  if (!isNew) {
    const existing = (allRules || []).find((rule) => rule.id === id) || publishedBaseline;
    if (existing && contentControlFingerprint(existing) === contentControlFingerprint({ ...snapshot, id })) {
      return { rules: allRules, record: normalizeContentControlForm(existing), unchanged: true, isNew: false };
    }
  }

  const nowIso = payload.lastUpdated || new Date().toISOString();
  const actor = payload.lastUpdatedBy || ACTOR.name;
  const record = {
    ...snapshot,
    id,
    dateAdded: isNew ? nowIso : publishedBaseline.dateAdded || nowIso,
    addedBy: isNew ? actor : publishedBaseline.addedBy || actor,
    lastUpdated: nowIso,
    lastUpdatedBy: actor,
    changeReason: String(payload.changeReason || '').trim(),
    testHistory: payload.testHistory || publishedBaseline?.testHistory || [],
  };
  try {
    emitRuleSaveHistory({
      isNew,
      previous: publishedBaseline,
      next: record,
      module: 'Configuration — Content Control',
      sourceModuleRef: 'configuration_content_control',
      changedItem: record.ruleName || record.name || id,
      itemId: id,
      changeReason: record.changeReason,
      actor,
      stamp: nowIso,
      compareKeys: [
        'ruleName',
        'name',
        'description',
        'module',
        'contentArea',
        'contentContext',
        'contentIssue',
        'triggers',
        'conditions',
        'detectionMethods',
        'blockMessage',
      ],
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }
  const rules = isNew
    ? [...allRules.filter((rule) => rule.id !== record.id), record]
    : allRules.map((rule) => (rule.id === record.id ? { ...rule, ...record } : rule));
  return { rules, record, unchanged: false, isNew };
}

export function mergeRulesAfterActivate(allRules, payload, publishedBaseline) {
  return mergeRulesAfterSave(allRules, payload, publishedBaseline).rules;
}

export function saveContentControlRule(allRules, payload, publishedBaseline, options = {}) {
  const excludeId = publishedBaseline?.id || payload?.id || undefined;
  const validation = collectContentControlSaveErrors(payload, allRules, excludeId, {
    originalLastUpdated: options.originalLastUpdated ?? publishedBaseline?.lastUpdated,
    originalScopeKey: options.originalScopeKey
      ?? (publishedBaseline ? contentControlScopeKey(publishedBaseline) : undefined),
    canSave: options.canSave !== false,
    checksAvailable: options.checksAvailable !== false,
  });
  if (validation.summary.length) {
    return {
      success: false,
      errors: validation.errors,
      summary: validation.summary,
      rules: allRules,
      unchanged: false,
    };
  }
  try {
    const merged = mergeRulesAfterSave(allRules, payload, publishedBaseline || {});
    return {
      success: true,
      errors: {},
      summary: [],
      rules: merged.rules,
      record: merged.record,
      unchanged: Boolean(merged.unchanged),
      isNew: Boolean(merged.isNew),
    };
  } catch {
    return {
      success: false,
      errors: { saveFailed: 'The Rule could not be saved. Your changes have been kept for review.' },
      summary: ['The Rule could not be saved. Your changes have been kept for review.'],
      rules: allRules,
      unchanged: false,
    };
  }
}

export function collectActivationErrors(formData, allRules, excludeId) {
  return collectContentControlSaveErrors(formData, allRules, excludeId).summary;
}

export function runContentControlRuleTest(formData, allRules) {
  const snapshot = ruleSnapshotFromForm(formData);
  const validation = collectContentControlSaveErrors(formData, allRules, formData.id || undefined);
  if (validation.summary.length) {
    return {
      passed: false,
      summary: 'Rule test blocked. Configuration conflicts or missing requirements were found.',
      detail: validation.summary.map((message) => `• ${message}`).join('\n'),
    };
  }
  return {
    passed: true,
    summary: 'Rule test passed. Configuration is valid — no live content was screened.',
    detail: [
      `Module: ${snapshot.module}`,
      `Content Area: ${snapshot.contentArea}`,
      `Content Issue: ${snapshot.contentIssue}`,
      `Detection Methods: ${snapshot.detectionMethods.join(', ') || '—'}`,
      '',
      'No duplicate, conflict or overlap issues detected.',
    ].join('\n'),
  };
}
